/**
 * @fileoverview API Endpoints Configuration
 * @description Centralized API endpoint definitions
 * @module services/api/endpoints
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },

  // Users
  USERS: {
    BASE: '/users',
    BY_ID: (id) => `/users/${id}`,
    PROFILE: '/users/profile',
    PREFERENCES: '/users/preferences',
  },

  // Projects (Example domain endpoints)
  PROJECTS: {
    BASE: '/projects',
    BY_ID: (id) => `/projects/${id}`,
    MEMBERS: (id) => `/projects/${id}/members`,
  },

  // GitHub API (v1)
  GITHUB: {
    HEALTH: '/v1/github/health',
    BRANCHES: (org, repo) => `/v1/github/repos/${org}/${repo}/branches`,
    COPILOT_METRICS: (org) => `/v1/github/orgs/${org}/copilot/metrics`,
    REPOSITORY: (org, repo) => `/v1/github/repos/${org}/${repo}`,
    ORG_REPOSITORIES: (org) => `/v1/github/orgs/${org}/repos`,
  },

  // Test Calendar API (v1)
  TEST_CALENDAR: {
    // Lookup: Teams
    TEAMS: '/v1/test-calendar/teams',
    TEAM_BY_ID: (id) => `/v1/test-calendar/teams/${id}`,

    // Lookup: Environments
    ENVIRONMENTS: '/v1/test-calendar/environments',
    ENVIRONMENT_BY_ID: (id) => `/v1/test-calendar/environments/${id}`,

    // Lookup: Test Types
    TEST_TYPES: '/v1/test-calendar/test-types',
    TEST_TYPE_BY_ID: (id) => `/v1/test-calendar/test-types/${id}`,

    // Calendar Events
    EVENTS: '/v1/test-calendar/events',
    EVENT_BY_ID: (id) => `/v1/test-calendar/events/${id}`,
    EVENT_STATUS: (id) => `/v1/test-calendar/events/${id}/status`,
    EVENT_EXTEND: (id) => `/v1/test-calendar/events/${id}/extend`,
    EVENT_CLOSE: (id) => `/v1/test-calendar/events/${id}/close`,

    // Event Sub-Resources
    EVENT_PARTICIPANTS: (id) => `/v1/test-calendar/events/${id}/participants`,
    EVENT_PARTICIPANT_BY_ID: (eventId, pid) => `/v1/test-calendar/events/${eventId}/participants/${pid}`,
    EVENT_COMMENTS: (id) => `/v1/test-calendar/events/${id}/comments`,
    EVENT_COMMENT_BY_ID: (eventId, cid) => `/v1/test-calendar/events/${eventId}/comments/${cid}`,
    EVENT_ATTACHMENTS: (id) => `/v1/test-calendar/events/${id}/attachments`,
    EVENT_ATTACHMENT_BY_ID: (eventId, aid) => `/v1/test-calendar/events/${eventId}/attachments/${aid}`,
    EVENT_EXTENSIONS: (id) => `/v1/test-calendar/events/${id}/extensions`,
    EVENT_AUDIT_LOG: (id) => `/v1/test-calendar/events/${id}/audit-log`,

    // Dashboard
    SUMMARY: '/v1/test-calendar/summary',

    // Event Subscriptions
    EVENT_SUBSCRIPTIONS: (id) => `/v1/test-calendar/events/${id}/subscriptions`,
    EVENT_SUBSCRIPTION_CHECK: (id) => `/v1/test-calendar/events/${id}/subscriptions/check`,
    USER_SUBSCRIPTIONS: '/v1/test-calendar/subscriptions/user',

    // Notifications
    NOTIFICATIONS: '/v1/test-calendar/notifications',
    NOTIFICATIONS_UNREAD_COUNT: '/v1/test-calendar/notifications/unread-count',
    NOTIFICATION_READ: (id) => `/v1/test-calendar/notifications/${id}/read`,
    NOTIFICATIONS_READ_ALL: '/v1/test-calendar/notifications/read-all',
  },

  // RBAC API (v1)
  RBAC: {
    HEALTH: '/v1/rbac/health',

    // Roles
    ROLES: '/v1/rbac/roles',
    ROLE_BY_ID: (id) => `/v1/rbac/roles/${id}`,
    ROLE_PERMISSIONS: (id) => `/v1/rbac/roles/${id}/permissions`,

    // Modules
    MODULES: '/v1/rbac/modules',
    MODULE_PERMISSIONS: (id) => `/v1/rbac/modules/${id}/permissions`,

    // User permissions (current user)
    MY_PERMISSIONS: '/v1/rbac/users/me/permissions',

    // User roles (admin)
    USER_ROLES: (userId) => `/v1/rbac/users/${encodeURIComponent(userId)}/roles`,
    REVOKE_USER_ROLE: (userId, roleId) => `/v1/rbac/users/${encodeURIComponent(userId)}/roles/${roleId}`,

    // User module access (admin)
    USER_MODULES: (userId) => `/v1/rbac/users/${encodeURIComponent(userId)}/modules`,
    REVOKE_USER_MODULE: (userId, moduleId) => `/v1/rbac/users/${encodeURIComponent(userId)}/modules/${moduleId}`,

    // Access requests
    ACCESS_REQUESTS: '/v1/rbac/access-requests',
    MY_ACCESS_REQUESTS: '/v1/rbac/access-requests/me',
    REVIEW_ACCESS_REQUEST: (id) => `/v1/rbac/access-requests/${id}`,
  },

  // Test Automation Leverage API (v1)
  TEST_AUTOMATION_LEVERAGE: {
    HEALTH: '/v1/test-automation-leverage/health',
    TEST_CASES: '/v1/test-automation-leverage/test-cases',
    SUMMARY: '/v1/test-automation-leverage/summary',
  },

  // Incident Analysis API (v1)
  INCIDENTS: {
    HEALTH: '/v1/incidents/health',
    LOAD: '/v1/incidents/load',
    RECORDS: '/v1/incidents/records',
    RECORDS_LATEST: '/v1/incidents/records/latest',
    RECORD_BY_ID: (id) => `/v1/incidents/records/${id}`,
    COLLECTION_STATUS: '/v1/incidents/collection/status',
    COLLECTION_BUILD: '/v1/incidents/collection/build',
    ANALYZE: '/v1/incidents/analyze',
    ANALYZE_SEMANTIC: '/v1/incidents/analyze/semantic',
  },

  // GitHub User Metrics API (v1)
  GITHUB_USER_METRICS: {
    HEALTH: '/v1/github-user-metrics/health',
    USERS: '/v1/github-user-metrics/users',
    USER_BY_NAME: (username) => `/v1/github-user-metrics/users/${username}`,
    METRICS: '/v1/github-user-metrics/metrics',
    USER_METRICS: (username) => `/v1/github-user-metrics/metrics/${username}`,
    REFRESH: '/v1/github-user-metrics/metrics/refresh',
  },

  // Report Logs API (v1)
  REPORT_LOGS: {
    HEALTH: '/v1/report-logs/health',
    LIST: '/v1/report-logs/list',
    FETCH: '/v1/report-logs/fetch',
    SEARCH: '/v1/report-logs/search',
    REPORTS: '/v1/report-logs/reports',
    EXECUTE: '/v1/report-logs/reports/execute',
    DOWNLOAD: '/v1/report-logs/reports/download',
    SOURCES: '/v1/report-logs/sources',
  },

  // App Insights API (v1)
  APP_INSIGHTS: {
    HEALTH: '/v1/app-insights/health',
    OVERVIEW: '/v1/app-insights/overview',
    REQUESTS: '/v1/app-insights/requests',
    EXCEPTIONS: '/v1/app-insights/exceptions',
    DEPENDENCIES: '/v1/app-insights/dependencies',
    AVAILABILITY: '/v1/app-insights/availability',
    EVENTS: '/v1/app-insights/events',
    PAGEVIEWS: '/v1/app-insights/pageviews',
    TRACES: '/v1/app-insights/traces',
    QUERY: '/v1/app-insights/query',
  },

  // Azure Function Health API (Created or modified by CoPilot AI Agent)
  AZURE_FUNCTION_HEALTH: {
    HEALTH: '/v1/azure-function-health/health',
    SCENARIOS: '/v1/azure-function-health/scenarios',
    PROBE: '/v1/azure-function-health/probe',
    FAVORITES: '/v1/azure-function-health/favorites',
    POD_NOTIFY: '/v1/azure-function-health/pod-notify',
  },
};

export default API_ENDPOINTS;
